import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/* ============================================================
   이름표 — shadcn 기본값을 우리 상태 자에 맞춰 갈아 끼웠다
   ------------------------------------------------------------
   받아온 판은 `rounded-full` 에 단색 바탕(`bg-primary`)이고 굵기가 500 이다.
   우리 것은 셋 다 다르다.

   - **모서리는 `--r-s`(4px)** 다. 알약 모양이 아니다.
   - **색은 세 겹**이다 — 옅은 바탕(`-t`) · 진한 글자(`-f`) · 중간 테두리(`-b`).
     단색 바탕에 흰 글자가 아니다. 이 세 겹은 **대비를 실측해 두 번 조정한
     값**이라 눈으로 고치면 안 된다(호박색 글자는 4.10:1 이라 한 눈금 내려
     4.59:1 로 맞췄다).
   - **굵기 500 은 맑은고딕에 없다.** 400 이다.

   갈래 이름(`gray`·`blue`·`amber`·`green`·`violet`·`red`)은 옛
   `.tag[data-c]` 와 **같은 이름을 그대로 쓴다.** 화면을 하나씩 옮기는 동안
   두 표기가 공존하는데, 이름이 같아야 옮길 때 값을 다시 판단하지 않는다.
   ============================================================ */

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-sm border px-s2 text-micro leading-[18px] font-normal whitespace-nowrap [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      tone: {
        gray: 'bg-st-gray-t text-st-gray-f border-st-gray-b',
        blue: 'bg-st-blue-t text-st-blue-f border-st-blue-b',
        amber: 'bg-st-amber-t text-st-amber-f border-st-amber-b',
        green: 'bg-st-green-t text-st-green-f border-st-green-b',
        violet: 'bg-st-violet-t text-st-violet-f border-st-violet-b',
        red: 'bg-st-red-t text-st-red-f border-st-red-b',
      },
    },
    defaultVariants: { tone: 'gray' },
  },
);


function Badge({
  className,
  tone,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ tone }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
